import Icon from '@material-ui/icons/AccountBalance';
import PeopleIcon from '@material-ui/icons/People';
import moment from 'moment';
import React from 'react';
import { CreateButton } from '../../buttons';
import { EditDialog, Filter, NestedResourceList, ResourceList, ResourceShow, SimpleShowLayout, Tab, TabbedShowLayout } from '../../controllers';
import { AgeField, BooleanField, DateTimeField, EnumField, ImageField, LinkField, TextField, TimeZoneField, TrialPeriodStartEndField } from '../../fields';
import { BooleanInput, EnumInput, ImageInput, InputGroup, ReferenceInput, SearchInput, TextInput } from '../../inputs';
import memberships from './memberships';
import subscriptions from './subscriptions';
import users from './users';

export default {
  Icon,
  dialogs: {
    edit: props => (
      <EditDialog
        {...props}
      >
        <InputGroup
          header="Summary"
        >
          <TextInput
            source="name"
            required
          />
          <EnumInput
            source="classification"
          />
          <EnumInput
            source="status"
          />

          <ImageInput
            label="Logo"
            source="logo"
          />
        </InputGroup>
        
        <InputGroup
          header="Referral Information"
          subHeader="If this user was referred by another Codeverse family, indicate by searching for the referring user or enter their referral code. Once set, this cannot be changed."
        >
          <ReferenceInput
            filter={{has_email: true }}
            label="Referrer Name"
            optionText={user => `${user.name} (${user.email})`}
            reference="users"
            sort="email"
            source="referred_by_id-as-users"
          />
          <TextInput
            label="Referral Code"
            source="referral_code"
          />
        </InputGroup>  
        
        <InputGroup
          header="Automatic account creation"
          subHeader="Allow kids to create their own accounts at this organization. This is most commonly used by schools."
        >
          <BooleanInput
            source="automatic_account_creation"
          />
        </InputGroup>

        <InputGroup
          header="Founding Family"
          subHeader="Is this organization part of the Founding Families program?"
        >
          <BooleanInput
            source="founding_family"
          />
        </InputGroup>

        <InputGroup
          header="Administration"
          subHeader="If this organization is a duplicate of another already in the system, select the original organization here. This will automatically delete the current organization."
        >
          <ReferenceInput
            label="Duplicate of"
            optionText={organizations => `${organizations.name} (${organizations.id}) - Created ${moment(organizations.created_at).format('lll')}`}
            reference="organizations"
            source="duplicate_of_id-as-organizations"
          />

        </InputGroup> 
      </EditDialog>
    ),
    create: props => (
      <CreateButton
        {...props}
      >
        <InputGroup
          header="Summary"
        >
          <TextInput
            source="name"
            required
          />
          <EnumInput
            source="classification"
          />
          <ImageInput
            label="Logo"
            source="logo"
          />
        </InputGroup>

        <InputGroup
          header="Referral Information"
          subHeader="If this user was referred by another Codeverse family, enter their referral code. Once set, this cannot be changed."
        >
          <TextInput
            label="Referral Code"
            source="referral_code"
          />
        </InputGroup>

        <InputGroup
          header="Automatic account creation"
          subHeader="Allow kids to create their own accounts at this organization. This is most commonly used by schools."
        >
          <BooleanInput
            source="automatic_account_creation"
          />
        </InputGroup>

        <InputGroup
          header="Founding Family"
          subHeader="Is this organization part of the Founding Families program?"
        >
          <BooleanInput
            source="founding_family"
          />
        </InputGroup>

      </CreateButton>
    ),
  },
  routes: {
    list:  props => (
      <ResourceList
        {...props}
        sort={{ field: 'created_at', order: 'DESC' }}
      >
        <TextField
          sortable={false}
          source="name"
        />
        <EnumField
          sortable={false}
          source="classification"
        />
        <EnumField
          sortable={false}
          source="status"
        />
        <DateTimeField
          source="created_at"
          sortable
        />
      </ResourceList>
    ),
    show: props => (
      <ResourceShow
        {...props}
      >
        <TabbedShowLayout>
          <Tab
            label="General"
          >
            <EnumField
              source="classification"
            />
            <EnumField
              source="status"
            />
            <TrialPeriodStartEndField />

            <LinkField
              label="Referrer"
              reference="users"
              source="referred_by_id"
            />

            <NestedResourceList
              perPage={10}
              reference="users"
            >
              <EnumField
                source="scope"
              />
              <TextField
                source="name"
              />
              <TextField
                source="email"
              />
              <AgeField
                showDateOfBirth
              />
              <LinkField
                source="rank_id"
              />
              <TextField
                label="Referral Code"
                source="referral_code"
              />
              <BooleanField
                source="verified"
              />
              <BooleanField
                source="ambassador"
              />
              <TimeZoneField
                source="time_zone"
              />
            </NestedResourceList>
          </Tab>

          <Tab
            label="Subscriptions"
          >
            <NestedResourceList
              reference="subscriptions"
            >
              <LinkField
                label="Child"
                parentReference="organizations"
                parentSource="organization_id"
                reference="users"
                source="user_id"
              />
              <EnumField
                source="tier"
              />
              <EnumField
                source="status"
              />
              <EnumField
                source="cadence"
              />
              <DateTimeField
                source="created_at"
              />
            </NestedResourceList>
          </Tab>

          <Tab
            label="Settings"
          >
            <TextField
              source="id"
            />
            <DateTimeField
              source="created_at"
              addLabel
            />

            <BooleanField
              source="automatic_account_creation"
              addLabel
            />

            <BooleanField
              source="founding_family"
              addLabel
            />

            <ImageField
              source="logo"
            />
          </Tab>

        </TabbedShowLayout>
      </ResourceShow>
    ),
  },
  components: {
    Filter: props => (
      <Filter
        {...props}
      >
        <SearchInput
          label="Search"
          source="q"
        />
        <EnumInput
          source="classification"
        />
        <EnumInput
          source="status"
        />
        <BooleanInput
          label="Deleted"
          source="deleted"
        />
      </Filter>
    ),
    Expand: props => (
      <SimpleShowLayout
        {...props}
      >
        <TrialPeriodStartEndField />
      </SimpleShowLayout>
    ),
  },
  resources: {
    subscriptions,
    // People subMenu
    users,
    memberships,
  },
  subMenus: [
    {
      name: 'People',
      Icon: PeopleIcon,
      resourceNames: [
        'users',
        'memberships',
      ],
    },
  ],
};
